import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

// Endpoints come from the environment. There is no fallback on purpose: a
// hardcoded default ends up committed, shipped in the bundle, and readable by
// anyone with the repo or devtools. If the variable is missing we want a loud
// failure in development, not a silent call to somebody's server.
//
//   VITE_HAILEY_ENDPOINT   chat
//   VITE_HAILEY_TTS        voice (optional)
const CHAT_URL = process.env.REACT_APP_HAILEY_ENDPOINT;
const TTS_URL = process.env.REACT_APP_HAILEY_TTS;
const API_KEY = process.env.REACT_APP_HAILEY_KEY;

if (!CHAT_URL && process.env.NODE_ENV !== "production") {
  console.error('VITE_HAILEY_ENDPOINT is not set — the assistant will not respond.');
}
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

export const GREETING =
  "Hi, I'm InstaPass AI. I help you discover live events, compare ticket options across marketplaces, monitor drops, recommend the best seats, and build your entire night—from parking to hotels—all in one conversation. Where do you want to go?";

function lastQuestion(text) {
  const parts = String(text || '').split(/(?<=[.?!])\s+/);
  const q = [...parts].reverse().find((p) => p.includes('?'));
  return (q || text || '').toLowerCase();
}

function answerSuggestions(haileyText, step) {
  const t = lastQuestion(haileyText);
  if (/new to instapass|used it before|new user|existing user/.test(t)) {
    return ['New user', 'Existing user', 'Skip'];
  }
  if (/how many|how many tickets|how many seats|\bquantity\b/.test(t)) {
    return ['2 tickets', '4 tickets', 'Skip'];
  }
  if (/first one|second one|which (one|show|event|date)/.test(t)) {
    return ['The first one', 'The second one', 'Skip'];
  }
  if (/email|e-mail/.test(t)) return ['Skip'];
  if (/where do you want|what (do you want|are you looking)|find (tickets|events|concerts)|what event/.test(t)) {
    return ['Find concerts', 'Sports this weekend', 'Events near me'];
  }
  if (/lock this|keep looking|that's right|confirm|all set|want me to|shall i|should i/.test(t)) {
    return ['Yes', 'No', 'Skip'];
  }
  if (/\?/.test(t)) return ['Yes', 'No', 'Skip'];
  if (step === 'user_type') return ['New user', 'Existing user', 'Skip'];
  if (step === 'event') return ['Find concerts', 'Sports this weekend', 'Events near me'];
  return [];
}

/* Renders one of Hailey's lines as individual words so the ones she has
 * already said can be lit while the rest stay dimmed.
 *
 * Only the line she is speaking right now is rendered this way — every other
 * message stays a plain text node. That keeps the DOM light and, more usefully,
 * means a finished message needs no cleanup pass to become readable again: it
 * simply stops being the active one.
 *
 * `said` is a word count, not an index, because that is what the follow loop
 * naturally produces (it advances a cursor past every word whose cue time has
 * passed). The last word inside the count is the one she is on.
 */
function SpokenText({ text, said }) {
  let k = -1;
  return (
    <>
      {String(text || '')
        .split(/(\s+)/)
        .map((part, idx) => {
          if (!part) return null;
          if (/^\s+$/.test(part)) return part;
          k += 1;
          const cls = k < said ? (k === said - 1 ? 'w said now' : 'w said') : 'w';
          return (
            <span key={idx} className={cls}>
              {part}
            </span>
          );
        })}
    </>
  );
}

function fmtDate(iso) {
  if (!iso) return 'Date TBA';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Date TBA';
  }
}

export const HaileyChat = forwardRef(function HaileyChat({ onOrbState, onAmp }, ref) {
  const [messages, setMessages] = useState([{ who: 'hailey', text: GREETING }]);
  const [convState, setConvState] = useState({ step: 'user_type' });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [listening, setListening] = useState(false);
  /* Which message she is reading and how many of its words she has reached.
     `i: -1` means nobody is being read, which is the same thing as "render
     every message as plain, fully-lit text". */
  const [speech, setSpeech] = useState({ i: -1, n: 0 });
  const threadRef = useRef(null);
  const composerRef = useRef(null);
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);
  const blessedRef = useRef(false);
  const greetedVoiceRef = useRef(false);
  const recRef = useRef(null);
  /* Amplitude rig — see readAmplitude() below. */
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const followRef = useRef(0);

  const setOrb = useCallback(
    (s) => {
      onOrbState?.(s);
    },
    [onOrbState],
  );

  /* ── Amplitude → orb ───────────────────────────────────────────────────
     Tap the playing audio with a WebAudio analyser and push RMS upward, so
     the orb's glow rides Hailey's actual voice instead of a fixed animation.

     Three things make this fussy, all handled here:
       · createMediaElementSource can be called ONCE per <audio> element. Call
         it twice and the browser throws. So the context, source and analyser
         are built on first use and kept in refs for the life of the panel.
       · Routing through the context means the element's own output is muted
         unless we connect to the destination — so we do, explicitly.
       · Autoplay policy suspends a context created before a user gesture.
         resume() is cheap and a no-op when it's already running.

     Any failure here is cosmetic, so the whole thing is wrapped: with no
     amplitude the orb's CSS tremble carries the speaking state on its own. */
  const readAmplitude = useCallback(
    (el) => {
      if (!onAmp || !el) return;
      try {
        if (!ctxRef.current) {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (!AC) return;
          ctxRef.current = new AC();
        }
        /* HARD RULE: do not touch her audio unless the context is actually
           running.

           createMediaElementSource() permanently reroutes an element's output
           through the audio graph — there is no undo. If the context is
           suspended (autoplay policy, when it wasn't created inside a user
           gesture), that reroute sends her voice into a graph that isn't
           processing, and the customer hears NOTHING. This exact bug shipped:
           the orb got its waveform and Hailey went silent.

           So the analyser is opt-in on a healthy context only. A running
           context gets the pretty amplitude; a suspended one is left strictly
           alone, the element plays normally, and the orb falls back to its CSS
           tremble. Silence is never an acceptable price for an animation. */
        if (ctxRef.current.state !== "running") {
          ctxRef.current.resume?.();
          if (ctxRef.current.state !== "running") return;
        }
        if (!analyserRef.current) {
          const src = ctxRef.current.createMediaElementSource(el);
          analyserRef.current = ctxRef.current.createAnalyser();
          analyserRef.current.fftSize = 256;
          src.connect(analyserRef.current);
          analyserRef.current.connect(ctxRef.current.destination);
        }
        ctxRef.current.resume?.();
        const analyser = analyserRef.current;
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (el.paused || el.ended) {
            onAmp(0);
            rafRef.current = 0;
            return;
          }
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i += 1) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          // ×3.2 maps speech-level RMS onto a usable 0..1 — same scaling the
          // concept build settled on after watching it against real playback.
          onAmp(Math.min(1, Math.sqrt(sum / buf.length) * 3.2));
          rafRef.current = requestAnimationFrame(tick);
        };
        if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
      } catch {
        /* no amplitude; the CSS fallback covers it */
      }
    },
    [onAmp],
  );

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ctxRef.current?.close?.();
    },
    [],
  );

  const blessAudio = useCallback(() => {
    if (blessedRef.current) return;
    blessedRef.current = true;
    const a = audioRef.current || (audioRef.current = new Audio());
    a.src = SILENT_WAV;
    a.play()?.catch(() => {});
    /* Create the AudioContext HERE, inside the user gesture that called us.
       A context created during a gesture starts "running"; one created later
       (e.g. when her reply arrives, seconds after the click) starts
       "suspended" and may never resume. Since readAmplitude refuses to touch
       her audio unless the context is running, doing this here is the
       difference between the orb reacting to her voice and it just trembling.
       Failure is fine — she still speaks, the orb just animates on its own. */
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && !ctxRef.current) ctxRef.current = new AC();
      ctxRef.current?.resume?.();
    } catch {
      /* no amplitude; voice unaffected */
    }
  }, []);

  /* ── Word follow ───────────────────────────────────────────────────────
     ElevenLabs hands back a start time for every *character* of the line it
     just voiced. Walk the same words that were sent, note where each one
     begins in that string, and read off its cue. Then a frame loop advances a
     cursor past every word whose cue has passed.

     The text sent to the voice and the text on screen are the same string,
     whitespace-collapsed the same way — that is the whole reason the highlight
     stays in step. Build the two separately and they drift by a word or two
     halfway through a paragraph, which reads as a bug.

     With no timings (the OpenAI fallback, or a provider hiccup) the words are
     simply left fully lit. Text that stays dimmed because a fetch failed is
     worse than no effect at all. */
  const follow = useCallback((el, spokenText, msgIndex, starts) => {
    cancelAnimationFrame(followRef.current);
    const clear = () => setSpeech({ i: -1, n: 0 });
    if (msgIndex == null || msgIndex < 0 || !el) return clear();

    const words = spokenText.split(' ').filter(Boolean);
    if (!words.length || !Array.isArray(starts) || !starts.length) return clear();

    const cue = [];
    let at = 0;
    words.forEach((w) => {
      cue.push(starts[Math.min(at, starts.length - 1)] || 0);
      at += w.length + 1; // +1 for the single space that rejoins them
    });

    let k = 0;
    setSpeech({ i: msgIndex, n: 0 });
    const tick = () => {
      const t = el.currentTime;
      while (k < words.length && cue[k] <= t) k += 1;
      // Returning the previous object when nothing moved lets React bail out,
      // so this is a few renders a second rather than sixty.
      setSpeech((p) => (p.i === msgIndex && p.n === k ? p : { i: msgIndex, n: k }));
      if (k < words.length && !el.ended && !el.paused) {
        followRef.current = requestAnimationFrame(tick);
      } else {
        clear();
      }
    };
    followRef.current = requestAnimationFrame(tick);
  }, []);

  const speak = useCallback(
    async (text, msgIndex = -1) => {
      // Collapse whitespace before anything else: this exact string is both
      // what she says and what the cue offsets are measured against.
      const clipped = String(text || '').replace(/\s+/g, ' ').trim().slice(0, 2000);
      if (!clipped) return;
      blessAudio();
      setOrb('speaking');
      cancelAnimationFrame(followRef.current);
      try {
        const res = await fetch(TTS_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(API_KEY ? { 'X-InstaPass-Key': API_KEY } : {}),
          },
          body: JSON.stringify({ role: 'instapass', text: clipped, timestamps: true }),
        });
        if (!res.ok) throw new Error('tts');

        // The endpoint answers JSON with base64 audio when timings were asked
        // for, and raw mp3 bytes otherwise. Accept both, so a server that has
        // not picked up the change yet still talks — just without the effect.
        let url;
        let starts = null;
        if ((res.headers.get('content-type') || '').includes('application/json')) {
          const j = await res.json();
          if (!j.audio) throw new Error('tts');
          const bin = atob(j.audio);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          url = URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
          starts = Array.isArray(j.starts) ? j.starts : null;
        } else {
          url = URL.createObjectURL(await res.blob());
        }

        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        const a = audioRef.current || (audioRef.current = new Audio());
        a.src = url;
        const done = () => {
          cancelAnimationFrame(followRef.current);
          setSpeech({ i: -1, n: 0 });
          onAmp?.(0);
          setOrb('idle');
        };
        a.onended = done;
        a.onerror = done;
        a.onplay = () => {
          readAmplitude(a);
          follow(a, clipped, msgIndex, starts);
        };
        await a.play();
      } catch {
        cancelAnimationFrame(followRef.current);
        setSpeech({ i: -1, n: 0 });
        onAmp?.(0);
        setOrb('idle');
      }
    },
    [blessAudio, setOrb, onAmp, readAmplitude, follow],
  );

  useImperativeHandle(ref, () => ({
    onOrbTap() {
      blessAudio();
      composerRef.current?.focus();
      if (!greetedVoiceRef.current) {
        greetedVoiceRef.current = true;
        speak(GREETING, 0); // the greeting is always the first message
      }
    },
  }));

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const send = useCallback(
    async (raw) => {
      const text = String(raw || '').trim();
      if (!text || busy) return;
      // Her reply lands after the user's line, so its index is fixed now.
      const botIndex = messages.length + 1;
      const history = messages.map((m) => ({
        role: m.who === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));
      setMessages((prev) => [...prev, { who: 'user', text }]);
      setInput('');
      setBusy(true);
      setError('');
      setOrb('thinking');
      try {
        const res = await fetch(CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(API_KEY ? { 'X-InstaPass-Key': API_KEY } : {}),
          },
          body: JSON.stringify({ message: text, history, state: convState, source: 'instapass_shop' }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Request failed');
        const reply = data.reply || "I'm sorry, I couldn't generate a response right now.";
        setConvState(data.state || convState);
        setMessages((prev) => [
          ...prev,
          {
            who: 'hailey',
            text: reply,
            events: data.events || [],
            partners: data.partners || null,
            summary: data.summary || null,
            travelChoices: data.travelChoices || null,
            support: data.support || null,
          },
        ]);
        speak(reply, botIndex);
      } catch (err) {
        console.error('Hailey:', err);
        setError('Sorry — try again in a moment.');
        setOrb('idle');
      } finally {
        setBusy(false);
      }
    },
    [busy, messages, convState, setOrb, speak],
  );

  const startMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      composerRef.current?.focus();
      return;
    }
    blessAudio();
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.onstart = () => {
      setListening(true);
      setOrb('listening');
    };
    rec.onend = () => {
      setListening(false);
      recRef.current = null;
      setOrb('idle');
    };
    rec.onerror = () => {
      setListening(false);
      setOrb('idle');
    };
    rec.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join(' ')
        .trim();
      if (transcript) send(transcript);
    };
    recRef.current = rec;
    rec.start();
  };

  const lastHailey = [...messages].reverse().find((m) => m.who === 'hailey');
  const chips = busy
    ? []
    : lastHailey?.travelChoices && lastHailey.travelChoices.length
      ? lastHailey.travelChoices
      : answerSuggestions(lastHailey?.text || '', convState.step);

  return (
    <div className="ip-chat" aria-label="InstaPass AI live ticket concierge">
      <div className="ip-chat__header">
        <div className="ip-chat__brand">
          <span className="ip-chat__dot" aria-hidden="true" />
          <div className="ip-chat__brand-copy">
            <strong>InstaPass AI</strong>
            <span>Hailey · Luxury ticket concierge</span>
          </div>
        </div>
        <span className="ip-chat__live">Live</span>
      </div>

      <div ref={threadRef} className="ip-chat__messages">
        {messages.map((m, i) => (
          <div key={i} className="space-y-2">
            <div
              className={
                m.who === 'user'
                  ? 'ip-msg ip-msg--user'
                  : `ip-msg ip-msg--bot${speech.i === i ? ' ip-msg--reading' : ''}`
              }
            >
              {m.who !== 'user' && speech.i === i ? (
                <SpokenText text={m.text} said={speech.n} />
              ) : (
                m.text
              )}
            </div>
            {!!(m.events && m.events.length) &&
              m.events.map((e) => {
                const where = [e.venue, e.city, e.state].filter(Boolean).join(', ');
                const href = e.url || `https://instapass.shop/event/${e.id}`;
                return (
                  <a key={e.id} href={href} target="_blank" rel="noopener noreferrer" className="ip-event-card">
                    <strong>{e.name}</strong>
                    <span>{fmtDate(e.occursAt)}</span>
                    {where && <span>{where}</span>}
                    <em>
                      {e.availableTickets != null
                        ? `${e.availableTickets.toLocaleString()} available · View tickets →`
                        : 'View tickets →'}
                    </em>
                  </a>
                );
              })}
            {m.summary && (
              <div className="ip-event-card">
                <strong>You’re all set</strong>
                <span>{m.summary.event}</span>
                <span>{m.summary.venue}</span>
                <span>{m.summary.date ? fmtDate(m.summary.date) : 'Date TBA'}</span>
                <span>{m.summary.price || 'See listing on event page'}</span>
                {m.summary.url && (
                  <a href={m.summary.url} target="_blank" rel="noopener noreferrer">
                    {m.summary.cta || 'Complete your purchase'}
                  </a>
                )}
              </div>
            )}
            {m.partners && (
              <div className="ip-event-card" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <span>{m.partners.intro}</span>
                {m.partners.partners.map((p) => (
                  <a
                    key={p.name}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    style={{ color: '#ff8a8a', marginRight: 10 }}
                  >
                    {p.name} →
                  </a>
                ))}
              </div>
            )}
            {m.support && (
              <a
                href={`tel:${String(m.support.phone || '').replace(/[^\d+]/g, '') || '+18442446782'}`}
                className="ip-support-card"
              >
                <strong>{m.support.agent || 'Sona'} · InstaPass support</strong>
                <span>{m.support.line || 'Call us and a team member can pick up.'}</span>
                <em>Call {m.support.phoneDisplay || '(844) 244-6782'} →</em>
              </a>
            )}
          </div>
        ))}
        {busy && (
          <div className="ip-msg ip-msg--bot ip-msg--typing">
            <i /><i /><i />
          </div>
        )}
        {error && <div style={{ color: '#ff8a8a', fontSize: 12 }}>{error}</div>}
      </div>

      {chips.length > 0 && (
        <div className="ip-chat__suggestions">
          {chips.map((q) => (
            <button key={q} type="button" onClick={() => send(q)}>
              {q}
            </button>
          ))}
        </div>
      )}

      <p className="ip-chat__partners">
        Save on flights and hotels when you book through InstaPass:{' '}
        <a
          href="https://expedia.com/affiliates/expedia-home.pQyftud"
          target="_blank"
          rel="noopener noreferrer sponsored"
        >
          Expedia
        </a>
        {' · '}
        <a
          href="https://www.hotels.com/affiliate/xsX0d2M"
          target="_blank"
          rel="noopener noreferrer sponsored"
        >
          Hotels.com
        </a>
      </p>

      <div className="ip-chat__input">
        <button
          type="button"
          className={`ip-chat__mic${listening ? ' listening' : ''}`}
          aria-label="Speak to InstaPass AI"
          title="Speak"
          onClick={startMic}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </button>
        <input
          ref={composerRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder="Ask me anything…"
          autoComplete="off"
        />
        <button type="button" className="ip-chat__send" onClick={() => send(input)} disabled={busy || !input.trim()}>
          Ask
        </button>
      </div>
    </div>
  );
});
